import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, RotateCcw, Check, ChevronDown, Heart, Palette, Droplet, Info, Loader2, Mail, Bookmark } from "lucide-react";
import { Reveal } from "./components";
import { QuoteCTA } from "./pages";
import { useAuth } from "./auth";
import { EmailQuizModal } from "./portal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "bhpc_color_quiz";

const opt = (value, label, desc) => ({ value, label, desc });

const QUESTIONS = [
  { id: "location", title: "Where are you painting?", subtitle: "This helps us recommend the right products and finishes.",
    options: [opt("interior", "Interior"), opt("exterior", "Exterior")] },
  { id: "room_type", title: "Which space is this for?", subtitle: "Different rooms flatter different colors.",
    options: ["Living Room", "Kitchen", "Bedroom", "Bathroom", "Dining Room", "Home Office", "Entryway", "Exterior", "Other"].map((r) => opt(r.toLowerCase().split(" ")[0], r)) },
  { id: "design_style", title: "What's your design style?", subtitle: "We'll match colors to the aesthetic you love.",
    options: ["Modern", "Contemporary", "Transitional", "Traditional", "Farmhouse", "Coastal", "Mid-Century", "Glam"].map((s) => opt(s.toLowerCase(), s)) },
  { id: "mood", title: "How do you want the room to feel?", subtitle: "The emotional tone guides the palette.",
    options: [opt("calm", "Calm & Serene"), opt("cozy", "Cozy & Inviting"), opt("fresh", "Fresh & Airy"), opt("sophisticated", "Sophisticated & Timeless"), opt("energizing", "Energizing & Bold"), opt("dramatic", "Dramatic & Moody")] },
  { id: "natural_light", title: "How much natural light does it get?", subtitle: "Light dramatically changes how color reads.",
    options: [opt("bright", "Bright", "Lots of sun most of the day"), opt("moderate", "Moderate", "Some direct light"), opt("low", "Low", "Little direct sunlight")] },
  { id: "window_direction", title: "Which way do the main windows face?", subtitle: "Optional — but it fine-tunes our advice.", optional: true,
    options: [opt("north", "North", "Cooler, flatter light"), opt("south", "South", "Warm, abundant light"), opt("east", "East", "Warm mornings"), opt("west", "West", "Golden afternoons"), opt("", "Not sure")] },
  { id: "color_family", title: "Any color family you're drawn to?", subtitle: "Pick a direction, or let us decide.",
    options: [opt("whites", "Whites"), opt("grays", "Grays"), opt("neutrals", "Greiges & Neutrals"), opt("blues", "Blues"), opt("greens", "Greens"), opt("warm", "Warm Tones"), opt("pinks_purples", "Pinks & Purples"), opt("any", "I'm open to anything")] },
  { id: "temp_pref", title: "Warm, cool, or neutral?", subtitle: "Not sure? Tap the cards below to learn — or let us infer it for you.", educational: true,
    options: [opt("warm", "Warm"), opt("cool", "Cool"), opt("neutral", "Neutral"), opt("not_sure", "I'm not sure")] },
  { id: "flooring", title: "What's your flooring like?", subtitle: "Optional — helps us coordinate with what stays.", optional: true,
    options: [opt("light_wood", "Light Wood"), opt("dark_wood", "Dark Wood"), opt("gray", "Gray / Cool"), opt("warm_stone", "Warm Tile / Stone"), opt("", "Not applicable")] },
  { id: "cabinetry", title: "And your cabinetry?", subtitle: "Optional — for kitchens, baths and built-ins.", optional: true,
    options: [opt("white", "White"), opt("wood", "Wood-toned"), opt("dark", "Dark"), opt("color", "Colorful"), opt("", "Not applicable")] },
  { id: "countertops", title: "Countertops?", subtitle: "Optional — we'll keep the palette cohesive.", optional: true,
    options: [opt("light", "White / Light"), opt("dark", "Dark"), opt("warm_stone", "Warm Stone"), opt("cool_gray", "Cool Gray"), opt("", "Not applicable")] },
  { id: "depth_pref", title: "How much color depth do you want?", subtitle: "From barely-there to bold.",
    options: [opt("light", "Light & Airy"), opt("medium", "Mid-tone"), opt("dark", "Deep & Dramatic"), opt("", "No preference")] },
  { id: "for_sale", title: "Are you preparing the home for sale?", subtitle: "If so, we'll lean into broadly appealing, resale-friendly colors.",
    options: [opt("yes", "Yes"), opt("no", "No")] },
  { id: "scope", title: "Is this a single room or the whole home?", subtitle: "This shapes how we think about flow.",
    options: [opt("single", "A single room"), opt("home", "The whole home")] },
];

const EDU_CARDS = [
  { key: "warm", title: "Warm", blurb: "Reds, oranges, yellows and golden browns.", feels: "Cozy, welcoming and intimate.", best: "North-facing or low-light rooms, living rooms, bedrooms and dining rooms.", swatches: ["#C9A227", "#B07A57", "#D8C3A5"] },
  { key: "cool", title: "Cool", blurb: "Blues, greens and soft violets.", feels: "Calm, crisp and airy.", best: "Bright, south or west-facing rooms, bathrooms, offices and serene spaces.", swatches: ["#8FB0B6", "#9DB4A0", "#B4CCC9"] },
  { key: "neutral", title: "Neutral", blurb: "Whites, grays, greiges and taupes.", feels: "Flexible, timeless and easy to live with.", best: "Whole-home backdrops and homes being prepared for sale.", swatches: ["#EDE7DC", "#C9C1B6", "#A9A297"] },
  { key: "not_sure", title: "I'm Not Sure", blurb: "That's completely okay.", feels: "We'll infer the best direction for you.", best: "We read your other answers, choose a direction, and explain exactly why on your results.", swatches: ["#D8C3A5", "#B4CCC9", "#C9C1B6"] },
];

function ProgressBar({ current, total }) {
  const pct = Math.round(((current + 1) / total) * 100);
  return (
    <div className="w-full">
      <div className="flex justify-between text-xs uppercase tracking-[0.14em] text-ink/50 mb-2">
        <span>Question {current + 1} of {total}</span>
        <span data-testid="quiz-progress-pct">{pct}%</span>
      </div>
      <div className="h-1 bg-black/10 w-full overflow-hidden">
        <div className="h-full bg-gold transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function EducationalCards() {
  const [open, setOpen] = useState(null);
  return (
    <div data-testid="edu-cards" className="grid sm:grid-cols-2 gap-3 mt-8">
      {EDU_CARDS.map((c) => (
        <div key={c.key} className="border border-black/10 bg-white">
          <button type="button" data-testid={`edu-toggle-${c.key}`} onClick={() => setOpen(open === c.key ? null : c.key)}
            className="w-full flex items-center justify-between px-5 py-4 text-left">
            <span className="flex items-center gap-3">
              <span className="flex -space-x-1">{c.swatches.map((s) => <span key={s} className="w-4 h-4 rounded-full border border-black/10" style={{ background: s }} />)}</span>
              <span className="font-serif text-lg">{c.title}</span>
            </span>
            <ChevronDown size={16} className={`transition-transform ${open === c.key ? "rotate-180" : ""}`} />
          </button>
          {open === c.key && (
            <div className="px-5 pb-5 text-sm text-ink/70 space-y-2" data-testid={`edu-content-${c.key}`}>
              <p>{c.blurb}</p>
              <p><span className="text-ink font-medium">How it feels:</span> {c.feels}</p>
              <p><span className="text-ink font-medium">Where it works:</span> {c.best}</p>
              <div className="flex gap-2 pt-1">{c.swatches.map((s) => <span key={s} className="h-8 flex-1 rounded" style={{ background: s }} />)}</div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function toggleFavorite(color) {
  const list = JSON.parse(localStorage.getItem("bhpc_favorites") || "[]");
  const exists = list.find((c) => c.code === color.code && c.brand === color.brand);
  let next;
  if (exists) { next = list.filter((c) => !(c.code === color.code && c.brand === color.brand)); toast("Removed from favorites"); }
  else { next = [...list, { brand: color.brand, code: color.code, name: color.name, hex: color.hex }]; toast.success("Saved to favorites"); }
  localStorage.setItem("bhpc_favorites", JSON.stringify(next));
  return !exists;
}

function ResultCard({ r, index }) {
  const [fav, setFav] = useState(false);
  const light = (r.lrv_estimated || 0) >= 55;
  return (
    <Reveal delay={index * 0.1}>
      <div data-testid={`result-card-${index}`} className="bg-white border border-black/10 shadow-[0_24px_60px_-32px_rgba(0,0,0,0.25)] flex flex-col h-full">
        <div className="h-44 relative" style={{ background: r.hex }}>
          <span className="absolute top-3 left-3 bg-white/90 text-ink text-[10px] font-bold uppercase tracking-widest px-3 py-1.5">{r.label}</span>
          <button data-testid={`fav-btn-${index}`} onClick={() => setFav(toggleFavorite(r))} aria-label="Save favorite"
            className={`absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition-colors ${fav ? "bg-gold text-white" : "bg-white/90 text-ink hover:bg-white"}`}>
            <Heart size={16} fill={fav ? "currentColor" : "none"} />
          </button>
          <span className={`absolute bottom-3 left-3 text-xs font-mono px-2 py-1 ${light ? "bg-ink/10 text-ink" : "bg-white/85 text-ink"}`}>{r.hex}</span>
        </div>
        <div className="p-6 flex flex-col flex-1">
          <p className="text-xs uppercase tracking-[0.14em] text-gold">{r.brand}</p>
          <h3 className="font-serif text-2xl mt-1">{r.name}</h3>
          <p className="text-sm text-ink/50 font-mono">{r.code}</p>
          <div className="flex flex-wrap gap-2 mt-4">
            <span className="text-[11px] uppercase tracking-wider border border-black/15 px-2.5 py-1">{r.undertone} undertone</span>
            <span className="text-[11px] uppercase tracking-wider border border-black/15 px-2.5 py-1">{r.depth}</span>
            {(r.lrv_verified || r.lrv_estimated) != null && (
              <span className="text-[11px] uppercase tracking-wider border border-black/15 px-2.5 py-1">
                {r.lrv_verified != null ? `LRV ${r.lrv_verified}` : `Est. LRV ${r.lrv_estimated}`}
              </span>
            )}
          </div>
          <p className="text-sm text-ink/70 mt-4 leading-relaxed">{r.undertone_notes}</p>
          {r.reasons?.length > 0 && (
            <ul className="mt-4 space-y-1.5">
              {r.reasons.map((reason) => <li key={reason} className="flex items-start gap-2 text-sm text-ink/80"><Check size={14} className="text-gold mt-0.5 shrink-0" /> {reason}</li>)}
            </ul>
          )}
          <div className="mt-4 space-y-2 text-sm text-ink/70">
            <p className="flex items-start gap-2"><Droplet size={14} className="text-gold mt-0.5 shrink-0" /> <span><span className="text-ink font-medium">Suggested sheen:</span> {r.suggested_sheen}</span></p>
            <p className="flex items-start gap-2"><Info size={14} className="text-gold mt-0.5 shrink-0" /> <span>{r.lighting_considerations}</span></p>
          </div>
          <div className="flex flex-wrap gap-2 mt-6 pt-5 border-t border-black/10">
            <Link to={`/colors?q=${encodeURIComponent(r.code)}`} data-testid={`view-details-${index}`} className="text-xs overline border border-ink px-4 py-2.5 hover:bg-ink hover:text-white transition-colors">View Details</Link>
            <button onClick={() => setFav(toggleFavorite(r))} className="text-xs overline border border-ink px-4 py-2.5 hover:bg-ink hover:text-white transition-colors">{fav ? "Saved" : "Save Favorite"}</button>
          </div>
        </div>
      </div>
    </Reveal>
  );
}

export function ColorQuiz() {
  const saved = useMemo(() => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); } catch { return {}; } }, []);
  const [step, setStep] = useState(saved.step || 0);
  const [answers, setAnswers] = useState(saved.answers || {});
  const [phase, setPhase] = useState(saved.phase || "quiz"); // quiz | loading | results
  const [result, setResult] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [saved2, setSaved2] = useState(false);
  const { user, http } = useAuth();

  const normalizedResults = useMemo(() => {
    if (!result?.results) return {};
    const keys = ["best_match", "safe_pick", "designer_pick"];
    const out = {};
    result.results.forEach((r, i) => { out[keys[i] || `pick_${i}`] = r; });
    return out;
  }, [result]);

  const saveToPortal = async () => {
    if (!user) { window.location.href = "/signup"; return; }
    try {
      await http.post("/portal/quiz-results", { kind: "color", answers, results: normalizedResults, title: "Color Quiz Result" });
      setSaved2(true); toast.success("Saved to your portal");
    } catch (e) { toast.error("Could not save to portal"); }
  };

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ step, answers, phase: phase === "results" ? "quiz" : phase }));
  }, [step, answers, phase]);

  const total = QUESTIONS.length;
  const q = QUESTIONS[step];
  const selected = answers[q?.id];

  const choose = (value) => {
    setAnswers((a) => ({ ...a, [q.id]: value }));
    if (step < total - 1) setTimeout(() => setStep((s) => Math.min(s + 1, total - 1)), 180);
  };

  const next = () => { if (step < total - 1) setStep((s) => Math.min(s + 1, total - 1)); else submit(); };
  const back = () => { if (phase === "results") { setPhase("quiz"); return; } if (step > 0) setStep((s) => s - 1); };

  const submit = async () => {
    setPhase("loading");
    try {
      const r = await axios.post(`${API}/quiz/recommend`, { answers });
      setResult(r.data);
      setPhase("results");
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error("Something went wrong generating your results. Please try again.");
      setPhase("quiz");
    }
  };

  const restart = () => {
    setAnswers({}); setStep(0); setResult(null); setPhase("quiz");
    localStorage.removeItem(STORAGE_KEY);
    window.scrollTo(0, 0);
  };

  const isLast = step === total - 1;
  const canProceed = q?.optional || selected !== undefined;

  if (phase === "results" && result) {
    return (
      <div data-testid="quiz-results-page" className="pt-32 bg-ivory min-h-screen">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20">
          <Reveal>
            <p className="eyebrow text-gold">Your Color Matches</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-3 leading-tight">Three colors chosen for you</h1>
            <p className="text-muted-foreground mt-5 max-w-2xl leading-relaxed">Real, verified Sherwin-Williams and Benjamin Moore colors, hand-scored against everything you told us.</p>
            {result.inference_note && (
              <p data-testid="inference-note" className="mt-4 text-sm bg-white border-l-2 border-gold px-4 py-3 max-w-2xl text-ink/70"><Info size={14} className="inline text-gold mr-2" />{result.inference_note}</p>
            )}
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mt-10">
            {result.results.map((r, i) => <ResultCard key={r.label} r={r} index={i} />)}
          </div>

          {/* PAID FUNNEL */}
          <Reveal>
            <div data-testid="palette-funnel" className="bg-ink text-white mt-14 p-8 md:p-14 flex flex-col lg:flex-row lg:items-center gap-8 justify-between">
              <div className="max-w-2xl">
                <p className="eyebrow text-gold flex items-center gap-2"><Palette size={15} /> Go Beyond a Single Color</p>
                <h2 className="font-serif text-3xl md:text-4xl mt-3 leading-tight">Get a Designer-Curated Palette</h2>
                <p className="text-white/70 mt-4 leading-relaxed">Your quiz picks a single paint color. A designer-curated palette gives you the complete, coordinated story: wall, trim, ceiling, cabinet and accent colors, finish recommendations, and personal designer notes so every room comes together flawlessly.</p>
              </div>
              <Link to="/palettes" data-testid="explore-palettes-btn" className="shrink-0 inline-flex items-center justify-center gap-2 bg-gold text-white overline px-9 py-4 hover:bg-gold-dark transition-colors">Explore Designer Palettes <ArrowRight size={15} /></Link>
            </div>
          </Reveal>

          <div className="flex flex-wrap gap-4 mt-10">
            <button onClick={saveToPortal} data-testid="quiz-save-portal-btn" className="inline-flex items-center gap-2 bg-ink text-white overline px-8 py-4 hover:bg-gold transition-colors">
              <Bookmark size={15} /> {saved2 ? "Saved to Portal" : user ? "Save to My Portal" : "Save to Portal (Sign up)"}
            </button>
            <button onClick={() => setEmailOpen(true)} data-testid="quiz-email-btn" className="inline-flex items-center gap-2 border border-ink text-ink overline px-8 py-4 hover:bg-ink hover:text-white transition-colors">
              <Mail size={15} /> Email my results
            </button>
            <button onClick={restart} data-testid="quiz-restart-btn" className="inline-flex items-center gap-2 border border-ink text-ink overline px-8 py-4 hover:bg-ink hover:text-white transition-colors"><RotateCcw size={15} /> Retake the Quiz</button>
            <Link to={`/sheen-quiz?surface=${answers.location === "exterior" ? "exterior" : "walls"}${answers.room_type ? `&room=${encodeURIComponent(answers.room_type)}` : ""}`} data-testid="quiz-to-sheen-btn" className="inline-flex items-center gap-2 overline text-ink px-2 py-4 hover:text-gold transition-colors">Now find your ideal paint finish <ArrowRight size={15} /></Link>
            <Link to="/colors" className="inline-flex items-center gap-2 overline text-ink px-2 py-4 hover:text-gold transition-colors">Browse the full color library <ArrowRight size={15} /></Link>
          </div>

          {result.disclaimer && <p data-testid="quiz-disclaimer" className="text-xs text-ink/45 mt-10 max-w-3xl leading-relaxed">{result.disclaimer}</p>}
        </div>
        <QuoteCTA />
        <EmailQuizModal open={emailOpen} onClose={() => setEmailOpen(false)} kind="color" results={normalizedResults} />
      </div>
    );
  }

  return (
    <div data-testid="color-quiz-page" className="pt-32 bg-ivory min-h-screen">
      <div className="max-w-3xl mx-auto px-6 py-12 md:py-16">
        <Reveal>
          <p className="eyebrow text-gold">Interactive Color Quiz</p>
          <h1 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight">Find Your Perfect Paint Color</h1>
          <p className="text-muted-foreground mt-4 leading-relaxed">Answer a few quick questions and we&apos;ll recommend real, verified Sherwin-Williams and Benjamin Moore colors, chosen just for you. No account needed.</p>
        </Reveal>

        <div className="mt-10">
          <ProgressBar current={step} total={total} />
        </div>

        {phase === "loading" ? (
          <div className="py-24 text-center" data-testid="quiz-loading">
            <Loader2 className="animate-spin mx-auto text-gold" size={40} />
            <p className="font-serif text-2xl mt-6">Matching your colors…</p>
          </div>
        ) : (
          <div key={q.id} className="mt-10 bg-white border border-black/10 p-6 sm:p-9">
            <h2 className="font-serif text-2xl md:text-3xl leading-snug">{q.title}</h2>
            {q.subtitle && <p className="text-sm text-ink/55 mt-2">{q.subtitle}</p>}

            <div className="grid sm:grid-cols-2 gap-3 mt-7">
              {q.options.map((o) => {
                const active = selected === o.value;
                return (
                  <button key={o.label} type="button" data-testid={`quiz-opt-${q.id}-${o.value || "none"}`} onClick={() => choose(o.value)}
                    className={`text-left border px-5 py-4 transition-colors ${active ? "border-ink bg-ink text-white" : "border-black/15 bg-white hover:border-gold"}`}>
                    <span className="flex items-center justify-between gap-2">
                      <span className="font-medium">{o.label}</span>
                      {active && <Check size={16} className="text-gold shrink-0" />}
                    </span>
                    {o.desc && <span className={`block text-xs mt-1 ${active ? "text-white/60" : "text-ink/45"}`}>{o.desc}</span>}
                  </button>
                );
              })}
            </div>

            {q.educational && <EducationalCards />}

            <div className="flex items-center justify-between mt-9">
              <button type="button" onClick={back} disabled={step === 0} data-testid="quiz-back-btn"
                className={`inline-flex items-center gap-2 overline px-6 py-3.5 transition-colors ${step === 0 ? "text-ink/30 cursor-not-allowed" : "text-ink hover:text-gold"}`}>
                <ArrowLeft size={15} /> Back
              </button>
              <button type="button" onClick={next} disabled={!canProceed} data-testid="quiz-next-btn"
                className={`inline-flex items-center gap-2 overline px-9 py-4 transition-colors ${canProceed ? "bg-ink text-white hover:bg-gold" : "bg-black/10 text-ink/40 cursor-not-allowed"}`}>
                {isLast ? "See My Colors" : "Next"} <ArrowRight size={15} />
              </button>
            </div>
          </div>
        )}

        {phase !== "loading" && (
          <button onClick={restart} className="mt-6 text-xs text-ink/40 hover:text-ink inline-flex items-center gap-1.5" data-testid="quiz-startover-link"><RotateCcw size={12} /> Start over</button>
        )}
      </div>
    </div>
  );
}
